/**
 * =============================================================================
 * ROASTBREW - PLAYLIST  ·  /playlist/:code
 * =============================================================================
 *
 * One playlist: title, author, description, then its Contents — lessons numbered
 * in order, with teachers' notes as quiet lines between them.
 *
 *   Play all  → /playlist/:code/1
 *   Any item  → /playlist/:code/:item   (item = position in the list, notes included)
 *   Save      → toggles on this page only (not saved anywhere yet)
 *   Remix     → /playlist/:newCode/edit, opened on a copy of this playlist
 *   Edit      → /playlist/:code/edit    (shown instead of Save on your own playlists)
 *
 * NOT WIRED YET. Content comes from data/playlistSample.js.
 * Access: gated in App.jsx (RequireFeature 'playlists'). No tier logic here.
 * =============================================================================
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { getSamplePlaylist, numberItems, newPlaylistCode } from './data/playlistSample';

const SANS = '"DM Sans", sans-serif';
const SERIF = '"Cormorant Garamond", serif';
const PENCIL = (size) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" />
  </svg>
);

export default function PlaylistPage({ isMobile = false, isTablet = false }) {
  const { colors, isLight } = useTheme();
  const navigate = useNavigate();
  const { code } = useParams();
  const [saved, setSaved] = useState(false);

  const playlist = getSamplePlaylist(code);
  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const rule = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)';
  const leader = isLight ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.22)';

  const page = {
    padding: isMobile ? '80px 16px 48px' : isTablet ? '80px 32px 56px' : '40px 48px 80px',
    minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
    background: colors.bg, color: colors.text, animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };
  const backLink = (
    <button type="button" onClick={() => navigate('/playlist')} style={{
      display: 'inline-flex', alignItems: 'center', gap: '10px', minHeight: '44px', padding: 0, border: 0, background: 'transparent',
      fontFamily: SANS, fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.muted, cursor: 'pointer',
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      All playlists
    </button>
  );

  if (!playlist) {
    return (
      <div className="pl-page" style={page}>
        <div style={{ maxWidth: '780px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {backLink}
          <h1 style={{ margin: 0, fontFamily: SERIF, fontWeight: 300, fontSize: '44px' }}>We couldn’t find this playlist</h1>
          <p style={{ margin: 0, fontFamily: SANS, fontSize: '15px', lineHeight: 1.6, color: colors.muted }}>
            There’s no playlist with the code {code}. Check the code, or find one from your playlists.
          </p>
        </div>
      </div>
    );
  }

  const items = numberItems(playlist.items);
  const lessons = items.filter((i) => i.type === 'lesson').length;
  const notes = items.length - lessons;
  const course = (items.find((i) => i.type === 'lesson') || {}).course;
  const mine = playlist.author === 'You';

  const quiet = {
    display: 'inline-flex', alignItems: 'center', gap: '10px', height: '50px', padding: '0 22px', borderRadius: '999px',
    border: `1px solid ${edge}`, background: 'transparent', color: colors.textSecondary,
    fontFamily: SANS, fontSize: '14.5px', fontWeight: 500, cursor: 'pointer',
  };

  return (
    <div className="pl-page" style={page}>
      <div style={{ maxWidth: '780px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: isMobile ? '36px' : '52px' }}>
        {backLink}

        <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px', textAlign: 'center' }}>
          {course && <span style={{ fontFamily: SANS, fontSize: '12px', fontWeight: 600, letterSpacing: '0.26em', textTransform: 'uppercase', color: colors.accent }}>Playlist · {course}</span>}
          <h1 style={{ margin: 0, fontFamily: SERIF, fontWeight: 300, fontSize: isMobile ? '44px' : '68px', lineHeight: 0.98, letterSpacing: '-0.01em' }}>{playlist.title}</h1>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '21px', color: colors.textSecondary }}>by {playlist.author}</div>
          {playlist.description && (
            <p style={{ margin: 0, maxWidth: '560px', fontFamily: SANS, fontSize: '16px', lineHeight: 1.7, color: colors.muted }}>{playlist.description}</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: '8px' }}>
            <button type="button" onClick={() => navigate(`/playlist/${playlist.code}/1`)} style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px', height: '50px', padding: '0 26px', border: 0, borderRadius: '999px',
              background: colors.accent, color: isLight ? '#FFFFFF' : '#0D0D0D', fontFamily: SANS, fontSize: '15px', fontWeight: 600, cursor: 'pointer',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
              Play all
            </button>
            {mine ? (
              <button type="button" onClick={() => navigate(`/playlist/${playlist.code}/edit`)} style={quiet}>{PENCIL(15)} Edit</button>
            ) : (
              <button type="button" aria-pressed={saved} onClick={() => setSaved((v) => !v)} style={quiet}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h12v18l-6-4-6 4V3z" /></svg>
                {saved ? 'Saved' : 'Save'}
              </button>
            )}
            <button type="button" onClick={() => navigate(`/playlist/${newPlaylistCode()}/edit`, { state: { remixOf: playlist.code } })} style={quiet}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="6" cy="5" r="2" /><circle cx="6" cy="19" r="2" /><circle cx="18" cy="8" r="2" /><path d="M6 7v10" /><path d="M18 10c0 4-6 3-11.5 7" />
              </svg>
              Remix
            </button>
          </div>
        </header>

        <section aria-label="Contents" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '6px' }}>
            <h2 style={{ margin: 0, fontFamily: SANS, fontSize: '12px', fontWeight: 600, letterSpacing: '0.24em', textTransform: 'uppercase', color: colors.muted }}>Contents</h2>
            <div style={{ flex: 1, height: '1px', background: rule }} />
            <span style={{ fontFamily: SANS, fontSize: '13px', color: colors.muted }}>
              {lessons} {lessons === 1 ? 'lesson' : 'lessons'}{notes ? ` · ${notes} ${notes === 1 ? 'note' : 'notes'}` : ''}
            </span>
          </div>

          {items.map((it, i) => (
            <button key={it.type === 'note' ? it.id : it.videoId} type="button" onClick={() => navigate(`/playlist/${playlist.code}/${i + 1}`)} style={{
              display: 'grid', gridTemplateColumns: '52px minmax(0, 1fr)', gap: '16px', alignItems: 'baseline', width: '100%',
              padding: it.type === 'note' ? '14px 0' : '20px 0', border: 0, borderBottom: `1px solid ${rule}`, background: 'transparent',
              color: colors.text, textAlign: 'left', cursor: 'pointer',
            }}>
              {it.type === 'lesson' ? (
                <>
                  <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '26px', color: colors.accent }}>{it.lessonNo}.</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                      <span style={{ fontFamily: SERIF, fontSize: isMobile ? '21px' : '24px', lineHeight: 1.15 }}>{it.title}</span>
                      <span aria-hidden="true" style={{ flex: 1, minWidth: '12px', borderBottom: `1px dotted ${leader}`, transform: 'translateY(-5px)' }} />
                      <span style={{ fontFamily: SANS, fontSize: '14px', color: colors.textSecondary }}>{it.length}</span>
                    </span>
                    <span style={{ fontFamily: SANS, fontSize: '12.5px', letterSpacing: '0.02em', color: colors.muted }}>{it.course} · {it.section}</span>
                  </span>
                </>
              ) : (
                <>
                  <span style={{ display: 'flex', paddingLeft: '4px', color: colors.accent }}>{PENCIL(14)}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '18px', lineHeight: 1.35, color: colors.textSecondary }}>{it.text}</span>
                    <span style={{ fontFamily: SANS, fontSize: '12px', letterSpacing: '0.02em', color: colors.muted }}>Note from {playlist.author}</span>
                  </span>
                </>
              )}
            </button>
          ))}
        </section>
      </div>

      <style>{`
        .pl-page button:focus-visible { outline: 2px solid ${colors.accent}; outline-offset: 4px; border-radius: 10px; }
        @media (prefers-reduced-motion: reduce) { .pl-page, .pl-page * { animation: none !important; transition: none !important; } }
      `}</style>
    </div>
  );
}
